/**
 * Pairing codes (docs/zivy-provoz-soutezi.md §7, R10): eight characters
 * of Crockford's base32, shown grouped („K7MX-4QPD") and as a QR, sent
 * as the eight characters alone („K7MX4QPD"). The
 * screen that shows a code generates it here and sends the server only its
 * hash, so a hall sequencer issues codes exactly like the cloud.
 *
 * Twin of `internal/service/device/code.go`; both read
 * `pairingCode.vectors.json`.
 */
const ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const LENGTH = 8;

/**
 * A code as typed, in its wire form (eight characters, upper case, no
 * separators: what `POST /api/live/pair` takes), or "" when it is no
 * code. The hall app sends only this; the server reads nothing looser.
 */
export function normalizePairingCode(typed: string): string {
  let out = "";
  for (let ch of typed.toUpperCase()) {
    if (ch === "-" || ch === " ") continue;
    if (ch === "O") ch = "0";
    if (ch === "I" || ch === "L") ch = "1";
    if (!ALPHABET.includes(ch)) return "";
    out += ch;
  }
  return out.length === LENGTH ? out : "";
}

/** A fresh random code, grouped for reading. */
export function newPairingCode(): string {
  const raw = crypto.getRandomValues(new Uint8Array(LENGTH));
  const chars = Array.from(raw, (v) => ALPHABET[v % ALPHABET.length]);
  return `${chars.slice(0, LENGTH / 2).join("")}-${chars.slice(LENGTH / 2).join("")}`;
}

/** What the server stores of a code: SHA-256 of its normal form, hex. */
export async function hashPairingCode(code: string): Promise<string> {
  const bytes = new TextEncoder().encode(normalizePairingCode(code));
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (b) =>
    b.toString(16).padStart(2, "0"),
  ).join("");
}

/**
 * What a pairing QR says (protocol PairingQr, read by parsePairingQr): the
 * server to pair with and the code in its wire form, exactly what the app
 * then sends to `POST /api/live/pair`. One builder for both apps: the
 * organizer's page in the cloud and the IT screen in the hall.
 */
export function pairingQrText(url: string, code: string): string {
  return JSON.stringify({
    cmfis: "pair",
    url,
    code: normalizePairingCode(code),
  });
}

